import {
  InProgressUnenrollmentDeviceType,
  SetUnenrollmentReasonPayload,
} from '@/reducers/inProgressUtilityProgramUnenrollments'
import { UtilityProgram } from '@/types/utilityProgram'
import {
  CMSUnenrollmentDeviceType,
  DEFAULT_ARTICLE_SUGGESTIONS,
  DEFAULT_SETTTING_SUGGESTIONS,
} from '../DeviceSuggestionCard.constants'
import { match } from 'ts-pattern'

function deviceTypeToCMSDeviceType(
  deviceType: InProgressUnenrollmentDeviceType,
): CMSUnenrollmentDeviceType {
  return match(deviceType)
    .with('vehicle', () => 'vehicle' as const)
    .with('charger', () => 'vehicleCharger' as const)
    .with('thermostat', () => 'thermostat' as const)
    .with('energySystem', () => 'battery' as const)
    .with('profile', () => 'program' as const)
    .exhaustive()
}

export function getSettingsForDevice(
  deviceType: InProgressUnenrollmentDeviceType,
  selectedReasons: SetUnenrollmentReasonPayload['reasons'],
): string[] {
  const cmsDeviceType = deviceTypeToCMSDeviceType(deviceType)

  const selectedReasonsForDevice = selectedReasons
    .filter((r) => r.deviceType === deviceType)
    .map((r) => r.reason)

  // Get unique settings for this device based on selected reasons
  const settingsForDevice = selectedReasonsForDevice
    .flatMap((reason) =>
      DEFAULT_SETTTING_SUGGESTIONS.filter(
        (suggestion) =>
          suggestion.device === cmsDeviceType && suggestion.reason === reason,
      ).map((suggestion) => suggestion.setting),
    )
    .filter((setting, index, self) => self.indexOf(setting) === index) // unique

  return settingsForDevice
}

export function getArticlesToShowForDevice(
  utilityProgram: UtilityProgram,
  deviceType: InProgressUnenrollmentDeviceType,
  selectedReasons: SetUnenrollmentReasonPayload['reasons'],
): { url: string; text: string }[] {
  const cmsDeflectionSpec =
    utilityProgram.view_config_json?.unenrollmentDeflection ?? []

  // Merge CMS deflection spec with defaults (CMS takes priority)
  const mergedDeflectionSpec = [
    ...cmsDeflectionSpec,
    ...DEFAULT_ARTICLE_SUGGESTIONS,
  ]

  const selectedReasonsForDevice = selectedReasons
    .filter((r) => r.deviceType === deviceType)
    .map((r) => r.reason)

  // if the cms defines an article with the same title for this device type,
  // we don't want to show that title twice
  const seenArticleTitles = new Set<string>()

  return (
    mergedDeflectionSpec
      .filter((d) => d !== undefined)
      // only the reasons from the cms for this device
      .filter((d) => d.device === deviceTypeToCMSDeviceType(deviceType))
      // only the reasons that match the reasons we have selected for this device
      .filter((d) =>
        d.reasons.some((r) => selectedReasonsForDevice.includes(r)),
      )
      // Remove duplicate articles (by title)
      .filter((d) => {
        if (!d.articleText) {
          return false
        }
        if (seenArticleTitles.has(d.articleText)) {
          return false
        }
        seenArticleTitles.add(d.articleText)
        return true
      })
      .map((d) => ({
        url: d.articleUrl,
        text: d.articleText ?? d.articleUrl,
      }))
  )
}
